import { useEffect, useState } from "react";
import api from "../lib/api";
import { Users, Smartphone, Globe, Trophy, Lock, CheckCircle2 } from "lucide-react";

/**
 * Iter 80 — compact stats card showing how far the user's referral
 * link has travelled. Loads `/api/me/share-stats` once and reloads it
 * every 60 s so ambassadors see numbers climb while they share.
 */
export default function ReferralStatsCard({ className = "" }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get("/me/share-stats")
        .then(({ data }) => { if (!cancelled) setStats(data); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!stats || !stats.referral_code) return null;

  const Metric = ({ label, value, Icon, testid }) => (
    <div className="flex-1 min-w-0 bg-white border border-[#CBD5E1] rounded-xl p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#64748B] font-bold">
        {Icon && <Icon className="w-3 h-3" />}{label}
      </div>
      <p className="text-xl font-bold text-[#0A2540] mt-1" style={{ fontFamily: "Outfit" }}>{value}</p>
    </div>
  );

  return (
    <div className={`bg-[#F8FAFC] border border-[#CBD5E1] rounded-2xl p-4 ${className}`} data-testid="referral-stats-card">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-[#0A2540]" />
        <p className="text-sm font-semibold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>Mes partages</p>
        <code className="ml-auto text-[11px] font-mono bg-[#0A2540] text-white px-2 py-0.5 rounded">{stats.referral_code}</code>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Metric label="Clics" value={stats.clicks_total} testid="rsc-clicks" />
        <Metric label="Android" value={stats.android} Icon={Smartphone} testid="rsc-android" />
        <Metric label="iOS" value={stats.ios} Icon={Smartphone} testid="rsc-ios" />
        <Metric label="Web" value={stats.web} Icon={Globe} testid="rsc-web" />
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="rsc-signups">
          <p className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">Inscriptions</p>
          <p className="text-xl font-bold text-emerald-600 mt-1" style={{ fontFamily: "Outfit" }}>{stats.signups_total}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="rsc-tft">
          <p className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">TFT gagnés</p>
          <p className="text-xl font-bold text-amber-700 mt-1" style={{ fontFamily: "Outfit" }}>
            {Number(stats.tft_earned || 0).toLocaleString("fr-FR")}
          </p>
        </div>
      </div>
      <MilestoneProgress stats={stats} />
    </div>
  );
}

const fmt = (n) => Number(n || 0).toLocaleString("fr-FR");

function MilestoneProgress({ stats }) {
  const list = stats.milestones || [];
  if (!list.length) return null;
  const next = stats.next_milestone;
  const pct = next
    ? Math.round(((stats.signups_total - next.previous_target) / (next.target - next.previous_target)) * 100)
    : 100;
  return (
    <div className="mt-3 rounded-xl p-3 bg-[#0A2540] text-white" data-testid="rsc-milestone">
      <div className="flex items-center gap-2">
        <Trophy className="w-4 h-4 text-[#FACC15]" />
        {next ? (
          <p className="text-sm font-semibold" data-testid="rsc-milestone-label">
            Encore <span className="text-[#FACC15]" data-testid="rsc-milestone-remaining">{next.remaining}</span> inscription{next.remaining > 1 ? "s" : ""} pour débloquer{" "}
            <span className="text-[#FACC15]">+{fmt(next.bonus)} TFT</span>
          </p>
        ) : (
          <p className="text-sm font-semibold" data-testid="rsc-milestone-label">Tous les paliers sont débloqués. Bravo, ambassadeur !</p>
        )}
      </div>
      <div className="h-2 rounded-full bg-white/15 mt-2 overflow-hidden">
        <div className="h-full bg-[#FACC15] rounded-full transition-[width] duration-700" style={{ width: `${Math.max(4, pct)}%` }} data-testid="rsc-milestone-bar" />
      </div>
      <div className="flex gap-1.5 mt-2 flex-wrap">
        {list.map((m) => (
          <span key={m.target} data-testid={`rsc-milestone-chip-${m.target}`}
            className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${m.reached ? "bg-[#FACC15] text-[#0A2540]" : "bg-white/10 text-white/70"}`}>
            {m.reached ? <CheckCircle2 className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
            {m.target} filleuls · +{fmt(m.bonus)}
          </span>
        ))}
      </div>
    </div>
  );
}
